import type { ReactNode } from 'react';

/**
 * 侧栏分组标题左侧的图标。
 *
 * 为什么是内联 SVG 而不是图标库：控制台至今没有图标依赖，为一个侧栏六个图标
 * 引一个包（外加 tree-shaking 配置）不划算；而且这些图形需要跟着文字颜色走，
 * 内联之后 `currentColor` 直接生效，不必在主题变量和图标 prop 之间搭桥。
 *
 * 只用 `stroke` 不用 `fill`：侧栏里 18px 的实心图形比描边看起来更重，
 * 五个实心图标挤在一起会盖过文字。描边宽度统一 1.6，视觉重量才一致。
 *
 * 每个图形**最多三个元素**，这是被截图逼出来的约束：第一版按常规的
 * "地球带经纬、盒子带折线、身份卡带小字"画，16px 渲染后内线互相粘连，
 * 五个图标糊成一排黑点，分不出谁是谁。侧栏图标只在扫视时有零点几秒的
 * 存在感，轮廓可辨远比细节完整重要 —— 所以宁可只留外框加一条能定性别的线。
 * 尺寸也从 16px 提到 18px，同一个原因。
 *
 * 形状都落在 16×16 的网格上（留 2.5 外边距），所以任意一个都对齐
 * 同一条左边缘和同一条视觉中线 —— 换来换去不会有一枚显得下沉。
 *
 * 「平台总览」这一组没有标题（只有一项），所以没有对应图形。
 */
export type NavGroupIconName =
  'platform' | 'content' | 'commerce' | 'customers' | 'site' | 'account';

const PATHS: Record<NavGroupIconName, ReactNode> = {
  /* 「平台总览」这一组没有标题行，图形永远用不上 —— 但仍然登记成 null，
     这样入参可以直传 NavGroupKey，调用处不用写类型断言。 */
  platform: null,
  /* 内容与外观：浏览器窗口。一条顶栏就够，不再画地址栏的小圆点 */
  content: (
    <>
      <rect x="2.5" y="3.6" width="13" height="9.8" rx="2" />
      <path d="M2.5 7.2h13" />
    </>
  ),
  /* 商品：购物袋。袋身 + 提手，比拼立体的盒子少一半线 */
  commerce: (
    <>
      <path d="M3.9 5.6h10.2l-1.1 8H5z" />
      <path d="M6.4 5.6a2.6 2.6 0 0 1 5.2 0" />
    </>
  ),
  /* 客户：两个人。后面那位只留半张脸，避免两个完整的圆挤在一起 */
  customers: (
    <>
      <circle cx="6.4" cy="5.9" r="2.4" />
      <path d="M2.3 13.4c0-2.2 1.9-3.6 4.1-3.6s4.1 1.4 4.1 3.6" />
      <path d="M11.4 4.4a2.4 2.4 0 0 1 0 3.9" />
    </>
  ),
  /* 站点：地球。经纬线不能省 —— 少了它们就只是个圆，和「商品」的袋底分不开 */
  site: (
    <>
      <circle cx="8" cy="8" r="5.7" />
      <path d="M2.4 8h11.2" />
      <path d="M8 2.3c1.7 1.6 2.6 3.6 2.6 5.7S9.7 12.1 8 13.7C6.3 12.1 5.4 10.1 5.4 8S6.3 3.9 8 2.3z" />
    </>
  ),
  /* 账号：钥匙。语义是"这是你的凭证"，同时和「客户」的人形拉开距离 */
  account: (
    <>
      <circle cx="5.7" cy="8" r="2.6" />
      <path d="M8.3 8h5.5M11.7 8v2.6M13.8 8v1.9" />
    </>
  ),
};

export function NavGroupIcon({ name }: { name: NavGroupIconName }) {
  if (!PATHS[name]) return null;
  return (
    <svg
      viewBox="0 0 16 16"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}
